import * as Sentry from '@sentry/nextjs';

interface StorageTransformer<T> {
  serialize: (value: T) => string;
  deserialize: (source: string) => T;
}

type JsonValue = string | number | Date | boolean | { [key in string]: JsonValue } | readonly JsonValue[] | null;

export function generateJsonTransformer<T extends JsonValue>(
  reviver?: (key: string, value: JsonValue) => JsonValue,
): StorageTransformer<T> {
  return {
    serialize: (value: T) => JSON.stringify(value),
    deserialize: (source: string) => JSON.parse(source, reviver),
  };
}

class CachedStorage<T> {
  private key: string;
  private transformer: StorageTransformer<T>;

  // undefined はまだ read してない、null は read したけど空
  private value: T | null | undefined = undefined;

  constructor(private storage: Storage, { key, transformer }: { key: string; transformer: StorageTransformer<T> }) {
    this.key = key;
    this.transformer = transformer;
    this.storage = storage;
  }

  public get = (): T | null => {
    if (!process.browser) {
      return null;
    }

    if (this.value === undefined) {
      const source = this.storage.getItem(this.key);
      this.value = source ? this.transformer.deserialize(source) : null;
    }

    return this.value;
  };

  public set = (value: T): void => {
    if (this.value !== value) {
      this.value = value;
      this.storage.setItem(this.key, this.transformer.serialize(value));
    }
  };

  public delete = (): void => {
    this.value = null;
    this.storage.removeItem(this.key);
  };
}

export class LocalStorage<T> extends CachedStorage<T> {
  constructor(params: { key: string; transformer: StorageTransformer<T> }) {
    const storage = typeof window !== 'undefined' ? window.localStorage : new SSRStorage();
    super(storage, params);
  }
}

export class SessionStorage<T> extends CachedStorage<T> {
  constructor(params: { key: string; transformer: StorageTransformer<T> }) {
    const storage = typeof window !== 'undefined' ? window.sessionStorage : new SSRStorage();
    super(storage, params);
  }
}

class SSRStorage implements Storage {
  length = 0;

  clear(): void {
    this.notifyToSentry();
  }
  getItem(key: string): string | null {
    this.notifyToSentry(key);
    return null;
  }
  key(_: number): string | null {
    this.notifyToSentry();
    return null;
  }
  removeItem(key: string): void {
    this.notifyToSentry(key);
  }
  setItem(key: string, _value: string): void {
    this.notifyToSentry(key);
  }
  private notifyToSentry(key?: string) {
    Sentry.withScope(() => {
      Sentry.setExtra('key', key);
      Sentry.captureMessage('Tried to read BrowserStorage on SSR.');
    });
  }
}
